// pages/MetalProcessing.jsx
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Paper,
  alpha,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material';
import { useState } from 'react';
import {
  Factory,
  Verified,
  GppGood,
  Diamond,
  TrendingUp,
  Security,
  WhatsApp,
  Email,
  PrecisionManufacturing,
  WorkspacePremium,
  Shield,
  Timeline,
  EmojiEvents,
  MonetizationOn,
  LocalShipping,
  Speed,
  Handshake,
  ThumbUp,
  CheckCircle,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Premium animations
const goldShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const floatSlow = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(20px, -15px) rotate(2deg);
  }
  66% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatMedium = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(-15px, -10px) rotate(-2deg);
  }
  66% {
    transform: translate(15px, 8px) rotate(1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatFast = keyframes`
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  50% {
    transform: translate(8px, -8px) scale(1.05);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
`;

const shimmerBackground = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0.3;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

// Abstract Background Component
const AbstractBackground = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 30%, #0a0a2a 0%, #0d0d35 50%, #060620 100%)',
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '-5%',
        width: '40%',
        height: '40%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatSlow} 25s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatMedium} 30s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, rgba(0,100,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatFast} 20s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(0,150,255,0.06) 0%, rgba(0,150,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatSlow} 22s ease-in-out infinite reverse`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: '200px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
        transform: 'rotate(25deg)',
        animation: `${shimmerBackground} 4s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '25%',
        right: '15%',
        width: '300px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
        transform: 'rotate(-15deg)',
        animation: `${shimmerBackground} 5s ease-in-out infinite 1s`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '60%',
        left: '10%',
        width: '150px',
        height: '150px',
        border: '1px solid rgba(212,175,55,0.08)',
        borderRadius: '50%',
        animation: `${floatMedium} 18s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        right: '25%',
        width: '100px',
        height: '100px',
        border: '1px solid rgba(0,150,255,0.1)',
        borderRadius: '50%',
        animation: `${floatFast} 15s ease-in-out infinite reverse`,
      }}
    />
    
    {[...Array(20)].map((_, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${2 + Math.random() * 4}px`,
          height: `${2 + Math.random() * 4}px`,
          background: `rgba(212, 175, 55, ${0.1 + Math.random() * 0.3})`,
          borderRadius: '50%',
          animation: `${floatFast} ${15 + Math.random() * 20}s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
    ))}
    
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }}
    />
  </Box>
);

const MetalProcessing = () => {
  const [tabValue, setTabValue] = useState(0);

  const products = {
    coins: [
      { name: 'Pentillion Gold Eagle', purity: '99.99%', weight: '1 oz', year: '2024', mintage: '50,000', premium: '3%', metal: 'Gold', diameter: '32.7mm', thickness: '2.8mm' },
      { name: 'Pentillion Silver Maple', purity: '99.99%', weight: '1 oz', year: '2024', mintage: '250,000', premium: '2%', metal: 'Silver', diameter: '38mm', thickness: '3.15mm' },
      { name: 'Pentillion Platinum Philharmonic', purity: '99.95%', weight: '1 oz', year: '2024', mintage: '25,000', premium: '4%', metal: 'Platinum', diameter: '32mm', thickness: '2.5mm' },
      { name: 'Pentillion Palladium Panda', purity: '99.95%', weight: '1 oz', year: '2024', mintage: '10,000', premium: '5%', metal: 'Palladium', diameter: '34mm', thickness: '2.3mm' },
    ],
    bars: [
      { name: 'Gold Cast Bar', purity: '99.99%', weight: '1 kg', dimensions: '117.5 x 52.5 x 12 mm', serialized: true, metal: 'Gold', annualProduction: '50,000 units' },
      { name: 'Gold Minted Bar', purity: '99.99%', weight: '100 g', dimensions: '47 x 27 x 5 mm', serialized: true, metal: 'Gold', annualProduction: '200,000 units' },
      { name: 'Silver Cast Bar', purity: '99.9%', weight: '5 kg', dimensions: '180 x 90 x 25 mm', serialized: true, metal: 'Silver', annualProduction: '25,000 units' },
      { name: 'Platinum Bar', purity: '99.95%', weight: '10 g', dimensions: '24 x 14 x 2 mm', serialized: true, metal: 'Platinum', annualProduction: '100,000 units' },
    ],
    custom: [
      'Private label coin minting for institutions',
      'Commemorative coins and medals',
      'Corporate gifts and awards',
      'Investment bars in bespoke sizes',
      'Design consultation and prototyping',
      'Serialized certification',
      'Custom packaging solutions',
      'Blockchain registration',
    ],
  };

  const capabilities = [
    { icon: <PrecisionManufacturing />, title: 'Precision Minting', description: 'State-of-the-art coining presses with 0.001mm tolerance', metric: '99.99% Accuracy' },
    { icon: <Verified />, title: 'Certified Purity', description: 'Every product independently verified and serialized', metric: 'LBMA Certified' },
    { icon: <GppGood />, title: 'Quality Control', description: 'Multi-stage inspection including XRF and fire assay', metric: 'Zero Defects' },
    { icon: <Security />, title: 'Secure Handling', description: 'Vault-grade security throughout production', metric: '24/7 Monitoring' },
    { icon: <Speed />, title: 'High-Volume Production', description: 'Industrial-scale minting capacity', metric: '50K coins/day' },
    { icon: <LocalShipping />, title: 'Global Distribution', description: 'Direct shipping to 50+ countries', metric: 'Insured Delivery' },
  ];

  const refineryProcesses = [
    { step: 1, name: 'Assaying', description: 'Determining metal content and purity', duration: '2-4 hours' },
    { step: 2, name: 'Smelting', description: 'High-temperature melting', duration: '4-6 hours' },
    { step: 3, name: 'Refining', description: 'Chemical purification process', duration: '8-12 hours' },
    { step: 4, name: 'Casting', description: 'Bar and ingot formation', duration: '2-3 hours' },
    { step: 5, name: 'Certification', description: 'Assay and serialization', duration: '2 hours' },
  ];

  // REMOVED: metrics array - no longer displaying the 4 metric cards

  const getMetalColor = (metal) => {
    switch(metal) {
      case 'Gold': return '#D4AF37';
      case 'Silver': return '#C0C0C0';
      case 'Platinum': return '#E5E4E2';
      case 'Palladium': return '#CED0D6';
      default: return '#D4AF37';
    }
  };

  const getMetalGradient = (metal) => {
    switch(metal) {
      case 'Gold': return 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)';
      case 'Silver': return 'linear-gradient(135deg, #C0C0C0 0%, #E8E8E8 100%)';
      case 'Platinum': return 'linear-gradient(135deg, #E5E4E2 0%, #FFFFFF 100%)';
      case 'Palladium': return 'linear-gradient(135deg, #CED0D6 0%, #E2E4E9 100%)';
      default: return 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)';
    }
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <AbstractBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section with Processing Background Image */}
        <Box
          sx={{
            minHeight: '55vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: 'url("/images/processing.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 10 } }}>
            <Stack spacing={2.5}>
              <Chip
                label="METAL PROCESSING & REFINING"
                icon={<PrecisionManufacturing sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.2),
                  color: '#D4AF37',
                  width: 'fit-content',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  backdropFilter: 'blur(5px)',
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                Precision Metal Processing & Refining
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#D4AF37',
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                  fontWeight: 600,
                }}
              >
                Transforming Raw Materials into Investment-Grade Products
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#FFFFFF', 0.85),
                  maxWidth: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                State-of-the-art refining and minting facility producing LBMA-accredited bullion, 
                collector coins, and bespoke metal products for global markets
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Metrics Dashboard Section - REMOVED */}

        {/* Facility Overview Section - NEW */}
        <Box sx={{ bgcolor: '#FFFFFF', py: 6 }}>
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.6s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="FACILITY OVERVIEW"
                    icon={<Factory sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.1),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, md: 36 },
                      fontWeight: 800,
                      color: '#0a0a2a',
                      mb: 2,
                    }}
                  >
                    Advanced Metal Processing Facility
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.7), mb: 3, lineHeight: 1.7 }}>
                    Our integrated metal processing facility combines advanced refining technology 
                    with precision minting capabilities, processing over 500 tons of precious metals annually.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Factory sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Processing Capacity</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>500+ tons/year</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <WorkspacePremium sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Purity Standard</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>99.99% Minimum</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Shield sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Security Level</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Military-Grade</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocalShipping sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Global Reach</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>50+ Countries</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.6s ease-out 0.2s forwards`, opacity: 0 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha('#D4AF37', 0.03)} 0%, ${alpha('#D4AF37', 0.01)} 100%)`,
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Processing Volume by Metal</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Gold (Annual)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>850,000 oz</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={85} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Silver (Annual)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>2.5M oz</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={75} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Platinum (Annual)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>180,000 oz</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={60} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Palladium (Annual)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>120,000 oz</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={50} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Box>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Refining Process Section - NEW */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="REFINING PROCESS"
                icon={<Timeline sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                From Ore to Investment-Grade Metal
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Our integrated refining process ensures maximum purity and recovery
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={3}>
              {refineryProcesses.map((process, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      animation: `${scaleIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: alpha('#FFFFFF', 0.05),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: alpha('#D4AF37', 0.4),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -15,
                          left: 20,
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          bgcolor: '#D4AF37',
                          color: '#0a0a2a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '0.9rem',
                        }}
                      >
                        {process.step}
                      </Box>
                      <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 800, mb: 1 }}>
                        {process.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 1 }}>
                        {process.description}
                      </Typography>
                      <Chip
                        label={`Duration: ${process.duration}`}
                        size="small"
                        sx={{
                          bgcolor: alpha('#D4AF37', 0.15),
                          color: '#D4AF37',
                          fontSize: '0.6rem',
                        }}
                      />
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Capabilities Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="OUR CAPABILITIES"
                icon={<Factory sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.1),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  color: '#0a0a2a',
                  mb: 2,
                }}
              >
                World-Class Manufacturing
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#0a0a2a', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Advanced technology meets traditional craftsmanship
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={4}>
              {capabilities.map((cap, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        textAlign: 'center',
                        height: '100%',
                        bgcolor: '#F8F9FC',
                        border: `1px solid ${alpha('#D4AF37', 0.12)}`,
                        borderRadius: 3,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          borderColor: alpha('#D4AF37', 0.35),
                          boxShadow: `0 20px 35px ${alpha('#D4AF37', 0.12)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3.5 }}>
                        <Box sx={{ fontSize: 48, color: '#D4AF37', mb: 2 }}>{cap.icon}</Box>
                        <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 700, mb: 1 }}>
                          {cap.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mb: 1.5 }}>
                          {cap.description}
                        </Typography>
                        <Chip
                          label={cap.metric}
                          size="small"
                          sx={{
                            bgcolor: alpha('#D4AF37', 0.1),
                            color: '#D4AF37',
                            fontSize: '0.65rem',
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Products Tabs Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="OUR PRODUCTS"
                icon={<Diamond sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                Premium Metal Products
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Investment-grade quality with exceptional craftsmanship
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>
            
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                centered
                sx={{
                  borderBottom: `1px solid ${alpha('#D4AF37', 0.15)}`,
                  '& .MuiTab-root': {
                    color: alpha('#0a0a2a', 0.6),
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    py: 2,
                    '&.Mui-selected': {
                      color: '#D4AF37',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#D4AF37',
                    height: 3,
                  },
                }}
              >
                <Tab label="Premium Coins" />
                <Tab label="Investment Bars" />
                <Tab label="Custom Minting" />
              </Tabs>

              <Box sx={{ p: 4 }}>
                {tabValue === 0 && (
                  <Grid container spacing={3}>
                    {products.coins.map((coin, index) => (
                      <Grid item xs={12} sm={6} md={6} key={index}>
                        <Box
                          sx={{
                            animation: `${scaleIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                            opacity: 0,
                          }}
                        >
                          <Card
                            elevation={0}
                            sx={{
                              bgcolor: '#F8F9FC',
                              border: `1px solid ${alpha(getMetalColor(coin.metal), 0.2)}`,
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-6px)',
                                borderColor: getMetalColor(coin.metal),
                                boxShadow: `0 12px 25px ${alpha(getMetalColor(coin.metal), 0.15)}`,
                              },
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Typography variant="h6" sx={{ color: getMetalColor(coin.metal), fontWeight: 800, mb: 2 }}>
                                {coin.name}
                              </Typography>
                              <Divider sx={{ mb: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Purity</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.purity}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Weight</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.weight}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Diameter</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.diameter}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Thickness</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.thickness}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Year</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.year}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Mintage</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.mintage}</Typography>
                                </Grid>
                              </Grid>
                              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    flex: 1,
                                    background: getMetalGradient(coin.metal),
                                    color: '#0a0a2a',
                                    fontWeight: 700,
                                    '&:hover': { transform: 'translateY(-2px)' },
                                  }}
                                >
                                  Buy Now
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    flex: 1,
                                    borderColor: '#D4AF37',
                                    color: '#D4AF37',
                                    fontWeight: 600,
                                  }}
                                >
                                  Request Quote
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {tabValue === 1 && (
                  <Grid container spacing={3}>
                    {products.bars.map((bar, index) => (
                      <Grid item xs={12} sm={6} md={6} key={index}>
                        <Box
                          sx={{
                            animation: `${scaleIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                            opacity: 0,
                          }}
                        >
                          <Card
                            elevation={0}
                            sx={{
                              bgcolor: '#F8F9FC',
                              border: `1px solid ${alpha(getMetalColor(bar.metal), 0.2)}`,
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-6px)',
                                borderColor: getMetalColor(bar.metal),
                                boxShadow: `0 12px 25px ${alpha(getMetalColor(bar.metal), 0.15)}`,
                              },
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Typography variant="h6" sx={{ color: getMetalColor(bar.metal), fontWeight: 800, mb: 2 }}>
                                {bar.name}
                              </Typography>
                              <Divider sx={{ mb: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Purity</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.purity}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Weight</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.weight}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Dimensions</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.dimensions}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Annual Production</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.annualProduction}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <Verified sx={{ color: '#D4AF37', fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ color: bar.serialized ? '#D4AF37' : alpha('#0a0a2a', 0.5), fontWeight: 700 }}>
                                      {bar.serialized ? "Serialized & Certified" : "Bulk Packaging"}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                              <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={{
                                  mt: 3,
                                  borderColor: '#D4AF37',
                                  color: '#D4AF37',
                                  fontWeight: 600,
                                  '&:hover': {
                                    borderColor: '#D4AF37',
                                    bgcolor: alpha('#D4AF37', 0.05),
                                  },
                                }}
                              >
                                Request Pricing
                              </Button>
                            </CardContent>
                          </Card>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {tabValue === 2 && (
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: '#F8F9FC',
                        border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                        borderRadius: 3,
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h5" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                          Bespoke Manufacturing
                        </Typography>
                        <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.7), mb: 3 }}>
                          Pentillion offers comprehensive custom minting services for institutions, governments, and private clients.
                        </Typography>
                        <Grid container spacing={2}>
                          {products.custom.map((item, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <CheckCircle sx={{ color: '#D4AF37', fontSize: 16 }} />
                                <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 500 }}>{item}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                        <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            startIcon={<Email />}
                            sx={{
                              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                              color: '#0a0a2a',
                              fontWeight: 700,
                              px: 4,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                                transform: 'translateY(-2px)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            Request Quote
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<WhatsApp />}
                            sx={{
                              borderColor: '#D4AF37',
                              color: '#D4AF37',
                              fontWeight: 700,
                              px: 4,
                              '&:hover': {
                                borderColor: '#D4AF37',
                                bgcolor: alpha('#D4AF37', 0.05),
                              },
                            }}
                          >
                            WhatsApp Inquiry
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </Paper>
          </Container>
        </Box>

        {/* Quality Assurance Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.6s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="QUALITY ASSURANCE"
                    icon={<Shield sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.1),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, md: 36 },
                      fontWeight: 800,
                      color: '#0a0a2a',
                      mb: 2,
                    }}
                  >
                    Uncompromising Quality
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.7), mb: 3, lineHeight: 1.7 }}>
                    Every product undergoes rigorous testing and certification before leaving our facilities.
                  </Typography>
                  <List>
                    {[
                      { icon: <Verified />, title: 'XRF Analysis', description: 'Real-time composition verification' },
                      { icon: <GppGood />, title: 'Fire Assay', description: 'Independent third-party testing' },
                      { icon: <Security />, title: 'Blockchain Tracking', description: 'End-to-end provenance' },
                      { icon: <Timeline />, title: 'Serialized Certification', description: 'Unique identifier per product' },
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box sx={{ color: '#D4AF37' }}>{item.icon}</Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography sx={{ color: '#0a0a2a', fontWeight: 700 }}>{item.title}</Typography>}
                          secondary={<Typography sx={{ color: alpha('#0a0a2a', 0.5), fontSize: '0.75rem' }}>{item.description}</Typography>}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.6s ease-out 0.2s forwards`, opacity: 0 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      bgcolor: '#F8F9FC',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    <WorkspacePremium sx={{ fontSize: 56, color: '#D4AF37', mb: 2, animation: `${pulse} 2s ease-in-out infinite` }} />
                    <Typography variant="h4" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                      Lifetime Authenticity Guarantee
                    </Typography>
                    <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.6), mb: 3 }}>
                      Every Pentillion product is backed by our comprehensive authenticity and purity guarantee.
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: alpha('#D4AF37', 0.15) }} />
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>Quality Rating</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>99.99%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={99.99}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha('#D4AF37', 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#D4AF37',
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>Client Satisfaction</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>98%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={98}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha('#D4AF37', 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#D4AF37',
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Stack>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Certifications Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Chip
                label="CERTIFICATIONS"
                icon={<EmojiEvents sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Industry Accreditations
              </Typography>
            </Box>
            
            <Grid container spacing={3} justifyContent="center">
              {[
                'LBMA Good Delivery',
                'LPPM Accredited',
                'ISO 9001:2024',
                'ISO 14001',
                'ISO 45001',
                'RJC Certified',
                'UN Global Compact',
                'Responsible Sourcing',
              ].map((cert, idx) => (
                <Grid item xs={6} sm={4} md={3} key={idx}>
                  <Card
                    elevation={0}
                    sx={{
                      textAlign: 'center',
                      p: 2.5,
                      bgcolor: alpha('#FFFFFF', 0.05),
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: alpha('#D4AF37', 0.4),
                        bgcolor: alpha('#FFFFFF', 0.08),
                      },
                    }}
                  >
                    <Verified sx={{ color: '#D4AF37', fontSize: 32, mb: 1 }} />
                    <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600, display: 'block' }}>
                      {cert}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 8 }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Card
              elevation={0}
              sx={{
                p: 5,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                borderRadius: 3,
              }}
            >
              <PrecisionManufacturing sx={{ color: '#D4AF37', fontSize: 48, mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                Ready to Start Your Order?
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.6), mb: 3 }}>
                Contact our processing team to discuss your specific requirements
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  color: '#0a0a2a',
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 20px ${alpha('#D4AF37', 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Processing Team
              </Button>
            </Card>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default MetalProcessing;